<section class="project-dashboard-screen">
    <h2 class="dashboard-page-title">Dashboard</h2>

    @if (loading) {
        <div class="dashboard-placeholder">Loading project dashboard...</div>
    } @else if (errorMessage) {
        <div class="dashboard-placeholder dashboard-error">{{ errorMessage }}</div>
    } @else if (projectDetails; as detail) {
        <section class="dashboard-section">
            <div class="section-head">
                <p class="section-title">Active Project</p>
            </div>

            <div class="projects-table">
                <div class="projects-head">
                    <div class="col expander-col">
                        <button type="button" class="grid-toggle-icon-btn" (click)="toggleDetailsPanel()">
                            <i class="ri-arrow-down-s-line" [class.expanded]="detailsExpanded"></i>
                        </button>
                    </div>
                    <div class="col project-col">Project</div>
                    <div class="col pnum-col">TMEIC P#s (EQ, CX, WR)</div>
                    <div class="col sm-col">Site Manager/Field Lead</div>
                    <div class="col address-col">Site Address</div>
                </div>

                <div class="project-row-wrap">
                    <div class="project-row">
                        <div class="col expander-col"></div>
                        <div class="col project-col project-name-wrap">
                            @if (getProjectStatusIndicatorClass(detail)) {
                                <span
                                    class="project-status-indicator"
                                    [ngClass]="getProjectStatusIndicatorClass(detail)"
                                    [title]="getProjectStatusIndicatorLabel(detail)"
                                    aria-hidden="true">
                                </span>
                            }
                            <span class="project-name">{{ detail.projectName }}</span>
                        </div>
                        <div class="col pnum-col row-value">{{ getProjectNumbers(detail) }}</div>
                        <div class="col sm-col row-value">{{ getCollapsedSiteManager(detail) }}</div>
                        <div class="col address-col row-value">{{ getSafeValue(detail.siteAddress) }}</div>
                    </div>

                    @if (detailsExpanded) {
                        <div class="project-detail-wrap">
                            <div class="project-detail-blocks">
                                @if (canView('CUSTOMER_CARD')) {
                                <div class="project-detail-grid">
                                    <div class="detail-item">
                                        <p class="detail-label">Customer</p>
                                        <p class="detail-value">{{ getSafeValue(detail.customerName) }}</p>
                                    </div>
                                    <div class="detail-item">
                                        <p class="detail-label">End User</p>
                                        <p class="detail-value">{{ getSafeValue(detail.endUserName) }}</p>
                                    </div>
                                    <div class="detail-item">
                                        <p class="detail-label">Site Contact</p>
                                        <p class="detail-value">{{ getSafeValue(detail.siteContact) }}</p>
                                    </div>
                                    <div class="detail-item">
                                        <p class="detail-label">Customer Contact</p>
                                        <p class="detail-value">{{ getSafeValue(detail.customerContactNumber) }}</p>
                                    </div>
                                    <div class="detail-item">
                                        <p class="detail-label">Customer Email</p>
                                        <p class="detail-value">{{ getSafeValue(detail.customerEmailAddress) }}</p>
                                    </div>
                                    <div class="detail-item">
                                        <p class="detail-label">Project Address</p>
                                        <p class="detail-value">{{ getSafeValue(detail.siteAddress) }}</p>
                                    </div>
                                </div>
                                }

                                @if (canView('TMCA_INFO_CARD')) {
                                <div class="project-detail-grid">
                                    <div class="detail-item">
                                        <p class="detail-label">TMCA Ops PM</p>
                                        <p class="detail-value">{{ getSafeValue(detail.projectManager) }}</p>
                                    </div>
                                    <div class="detail-item">
                                        <p class="detail-label">Project Engineer</p>
                                        <p class="detail-value">{{ getSafeValue(detail.engineer) }}</p>
                                    </div>
                                    <div class="detail-item">
                                        <p class="detail-label">Site Manager</p>
                                        <p class="detail-value">{{ getSafeValue(detail.siteManagers.join(', ')) }}</p>
                                    </div>
                                    <div class="detail-item">
                                        <p class="detail-label">Field Lead</p>
                                        <p class="detail-value">{{ getSafeValue(detail.fieldLeads.join(', ')) }}</p>
                                    </div>
                                    @if (!isGiProject(detail)) {
                                        <div class="detail-item">
                                            <p class="detail-label">MVPS Config</p>
                                            <p class="detail-value">{{ formatSkidConfiguration(detail.skidConfiguration) }}</p>
                                        </div>
                                        <div class="detail-item">
                                            <p class="detail-label">Inv Model</p>
                                            <p class="detail-value">{{ getSafeValue(detail.inverterModel) }}</p>
                                        </div>
                                        <div class="detail-item">
                                            <p class="detail-label">XFMR Model</p>
                                            <p class="detail-value">{{ getSafeValue(detail.transformerModel) }}</p>
                                        </div>
                                    }
                                    <div class="detail-item">
                                        <p class="detail-label">PWA Site Indicator</p>
                                        <p-toggleswitch [ngModel]="detail.isPwaSite" [disabled]="true" />
                                    </div>
                                </div>
                                }

                                @if (detail.managerNotes) {
                                    <div class="additional-info-wrap">
                                        <p class="detail-subtitle">Additional Information</p>
                                        <div class="additional-info-content" [innerHTML]="detail.managerNotes"></div>
                                    </div>
                                }

                                @if (canView('CHECKLIST_CARD')) {
                                <div class="detail-completion-grid">
                                    <div class="detail-item">
                                        <p class="detail-label">Checklist Verified</p>
                                        <p class="detail-value">
                                            {{ detail.completion.verified }} / {{ detail.completion.total }}
                                            ({{ getPercent(detail.completion.verifiedPercentage) }})
                                        </p>
                                    </div>
                                    <div class="detail-item">
                                        <p class="detail-label">Checklist Not Verified</p>
                                        <p class="detail-value">
                                            {{ detail.completion.notVerified }} / {{ detail.completion.total }}
                                            ({{ getPercent(detail.completion.notVerifiedPercentage) }})
                                        </p>
                                    </div>
                                </div>
                                }

                                @if (canView('COMPLETION_BY_PHASE')) {
                                <div class="phase-wrap">
                                    <p class="detail-subtitle">Completion By Phase</p>
                                    <div class="phase-grid">
                                        @for (phase of detail.phaseCompletion; track phase.phase) {
                                            <div class="phase-item">
                                                <p class="phase-name">{{ phase.phase }}</p>
                                                <p class="phase-value">{{ phase.verified }} / {{ phase.total }} ({{ getPercent(phase.verifiedPercentage) }})</p>
                                            </div>
                                        } @empty {
                                            <div class="phase-item">
                                                <p class="phase-name">No phase data</p>
                                                <p class="phase-value">-</p>
                                            </div>
                                        }
                                    </div>
                                </div>
                                }

                                @if (canView('PROJECT_SPECIFIC_ACTIVE_USERS')) {
                                <div class="users-wrap">
                                    <p class="detail-subtitle">Project Field Resources</p>
                                    <div class="users-grid">
                                        <div class="users-head">
                                            <div>Name</div>
                                            <div>Role</div>
                                            <div>Email</div>
                                            <div>Phone</div>
                                            <div>City / State</div>
                                        </div>
                                        @for (user of detail.activeUsers; track user.userId) {
                                            <div class="users-row">
                                                <div>{{ getSafeValue(user.fullName) }}</div>
                                                <div>{{ getSafeValue(user.roleName) }}</div>
                                                <div>{{ getSafeValue(user.email) }}</div>
                                                <div>{{ getSafeValue(user.phoneNumber) }}</div>
                                                <div>{{ getSafeValue(user.city) }} / {{ getSafeValue(user.state) }}</div>
                                            </div>
                                        } @empty {
                                            <div class="users-empty">No active field users found.</div>
                                        }
                                    </div>
                                </div>
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>
        </section>

        <section class="dashboard-progress-grid">
            @if (canView('INTEGRATION_PROGRESS')) {
            <article class="progress-card integration-card">
                <header class="progress-card-head integration-head">
                    <div class="progress-title-wrap">
                        <i class="ri-git-branch-line"></i>
                        <p class="progress-title">Integration Progress</p>
                    </div>
                </header>
                <div class="progress-card-body">
                    <div class="progress-rows">
                        @for (row of getIntegrationRows(); track row.label) {
                            <div class="progress-row">
                                <span>{{ row.label }}</span>
                                <strong>{{ row.count }}</strong>
                            </div>
                        }
                    </div>
                </div>
            </article>
            }

            @if (canView('CUSTOMER_PROGRESS')) {
            <article class="progress-card customer-card">
                <header class="progress-card-head customer-head">
                    <div class="progress-title-wrap">
                        <i class="ri-user-line"></i>
                        <p class="progress-title">Customer Progress</p>
                    </div>
                </header>
                <div class="progress-card-body">
                    @for (template of getCustomerTemplates(); track template.projectTemplateId) {
                        <div class="progress-project">
                            <button type="button" class="progress-project-head" (click)="toggleCustomerTemplate(template.projectTemplateId)">
                                <span>{{ template.templateName }}</span>
                                <i class="ri-arrow-down-s-line" [class.expanded]="expandedCustomerTemplateId === template.projectTemplateId"></i>
                            </button>
                            @if (expandedCustomerTemplateId === template.projectTemplateId) {
                                <div class="progress-rows">
                                    @for (row of getCustomerRows(template.projectTemplateId); track row.label) {
                                        <div class="progress-row">
                                            <span>{{ row.label }}</span>
                                            <strong>{{ row.count }}</strong>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            </article>
            }

            @if (canView('COMMISSIONING_PROGRESS')) {
            <article class="progress-card commission-card">
                <header class="progress-card-head commission-head">
                    <div class="progress-title-wrap">
                        <i class="ri-bill-line"></i>
                        <p class="progress-title">Commissioning Progress</p>
                    </div>
                </header>
                <div class="progress-card-body">
                    <div class="progress-rows">
                        @for (row of getCommissionRows(); track row.label) {
                            <div class="progress-row commission-progress-row">
                                <div class="commission-progress-top">
                                    <span>{{ row.label }}</span>
                                    <strong>{{ row.value }}</strong>
                                </div>
                                <div class="commission-progress-track">
                                    <span
                                        class="commission-progress-fill"
                                        [style.width.%]="getCommissionProgressPercent(row.value)">
                                    </span>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </article>
            }
        </section>

        @if (canView('WMR_DSR_WILDLIFE') || canView('NCR')) {
        <section class="forms-grid-section">
            @if (canView('WMR_DSR_WILDLIFE')) {
            <article class="forms-card">
                <p class="detail-subtitle">WMR</p>
                <div class="users-grid">
                    <div class="users-head forms-head">
                        <div>Name</div>
                        <div>Assigned By</div>
                        <div>Date</div>
                        <div>Status</div>
                    </div>
                    <div class="forms-scroll-body">
                        @if (isFormsLoading('WMR')) {
                            <div class="users-empty">Loading WMR forms...</div>
                        } @else {
                            @for (row of getFormsByShortCode('WMR'); track row.name + '-' + row.assignedBy + '-' + $index) {
                                <div class="users-row forms-row">
                                    <div>{{ getSafeValue(row.name) }}</div>
                                    <div>{{ getSafeValue(row.assignedBy) }}</div>
                                    <div>{{ formatProjectFormDate(row.date) }}</div>
                                    <div>{{ getSafeValue(row.status) }}</div>
                                </div>
                            } @empty {
                                <div class="users-empty">No forms found.</div>
                            }
                        }
                    </div>
                </div>
            </article>
            }

            @if (canView('NCR')) {
            <article class="forms-card">
                <p class="detail-subtitle">NCR</p>
                <div class="users-grid">
                    <div class="users-head forms-head">
                        <div>Name</div>
                        <div>Assigned By</div>
                        <div>Date</div>
                        <div>Status</div>
                    </div>
                    <div class="forms-scroll-body">
                        @if (isFormsLoading('NCR')) {
                            <div class="users-empty">Loading NCR forms...</div>
                        } @else {
                            @for (row of getFormsByShortCode('NCR'); track row.name + '-' + row.assignedBy + '-' + $index) {
                                <div class="users-row forms-row">
                                    <div>{{ getSafeValue(row.name) }}</div>
                                    <div>{{ getSafeValue(row.assignedBy) }}</div>
                                    <div>{{ formatProjectFormDate(row.date) }}</div>
                                    <div>{{ getSafeValue(row.status) }}</div>
                                </div>
                            } @empty {
                                <div class="users-empty">No forms found.</div>
                            }
                        }
                    </div>
                </div>
            </article>
            }
        </section>
        }

        @if (canView('CUMULATIVE_HOURS_PER_USER') || canView('HOURS_ALLOCATION_BY_CATEGORY') || canView('HOURS_ALLOCATION_BY_SKID')) {
        <section class="project-charts-grid">
            @if (canView('CUMULATIVE_HOURS_PER_USER')) {
            <section class="chart-card chart-card-full">
                <div class="chart-header">
                    <p class="chart-title">Cumulative Hours Per User</p>
                    <div class="chart-header-actions">
                        <div class="manhours-user-filter">
                            <p-multiselect
                                [options]="getManHoursUserOptions()"
                                [(ngModel)]="selectedManHoursUserIds"
                                (ngModelChange)="onManHoursUsersChange()"
                                optionLabel="label"
                                optionValue="value"
                                [placeholder]="'Users'"
                                appendTo="body"
                                [showClear]="true"
                                [maxSelectedLabels]="2"
                                [scrollHeight]="'240px'"
                                styleClass="manhours-user-multiselect">
                            </p-multiselect>
                        </div>
                        <div class="date-range-control">
                            <button type="button" class="chart-btn" (click)="toggleDateRangePopover()">
                                {{ getDateRangeLabel() }} <i class="ri-calendar-line"></i>
                            </button>
                            @if (showDateRangePopover) {
                                <div class="date-range-popover">
                                    <label class="date-field">
                                        <span>Start Date</span>
                                        <input type="date" [value]="dateRangeDraft.startDate" (input)="onDateRangeInput('startDate', $any($event.target).value)">
                                    </label>
                                    <label class="date-field">
                                        <span>End Date</span>
                                        <input type="date" [value]="dateRangeDraft.endDate" (input)="onDateRangeInput('endDate', $any($event.target).value)">
                                    </label>
                                    @if (dateRangeError) {
                                        <p class="date-range-error">{{ dateRangeError }}</p>
                                    }
                                    <div class="date-range-actions">
                                        <button type="button" class="chart-btn chart-btn-light" (click)="closeDateRangePopover()">Cancel</button>
                                        <button type="button" class="chart-btn" [disabled]="!canApplyDateRange()" (click)="applyDateRange()">Apply</button>
                                    </div>
                                </div>
                            }
                        </div>
                        <button type="button" class="chart-btn" (click)="downloadCumulativeHoursPerUser()">
                            <i class="ri-download-line"></i> Download
                        </button>
                    </div>
                </div>
                @if (analyticsLoading) {
                    <div class="dashboard-placeholder">Loading man hours analytics...</div>
                } @else if (analyticsErrorMessage) {
                    <div class="dashboard-placeholder dashboard-error">{{ analyticsErrorMessage }}</div>
                } @else {
                    <div class="project-chart-wrapper">
                        <p-chart
                            type="line"
                            [data]="manHoursChartData"
                            [options]="manHoursChartOptions"
                            [style]="{ height: '250px' }">
                        </p-chart>
                    </div>
                }
            </section>
            }

            @if (canView('HOURS_ALLOCATION_BY_CATEGORY')) {
            <section class="chart-card">
                <div class="chart-header">
                    <p class="chart-title">Hours Allocation By Category</p>
                </div>
                @if (analyticsLoading) {
                    <div class="dashboard-placeholder">Loading category breakdown...</div>
                } @else if (analyticsErrorMessage) {
                    <div class="dashboard-placeholder dashboard-error">{{ analyticsErrorMessage }}</div>
                } @else {
                    <div class="project-chart-wrapper doughnut-wrapper">
                        <p-chart
                            type="doughnut"
                            [data]="categoryBreakdownChartData"
                            [options]="categoryBreakdownChartOptions"
                            [style]="{ height: '250px' }">
                        </p-chart>
                    </div>
                }
            </section>
            }

            @if (canView('HOURS_ALLOCATION_BY_SKID')) {
            <section class="chart-card">
                <div class="chart-header">
                    <p class="chart-title">Hours Allocation by Equipment</p>
                    <div class="chart-header-actions">
                        <button type="button" class="chart-btn" (click)="showSkidBreakdownFullscreen = true">
                            <i class="ri-fullscreen-line"></i> Full Screen
                        </button>
                        <button type="button" class="chart-btn" (click)="downloadHoursAllocationBySkid()">
                            <i class="ri-download-line"></i> Download
                        </button>
                    </div>
                </div>
                @if (analyticsLoading) {
                    <div class="dashboard-placeholder">Loading MV Power Station breakdown...</div>
                } @else if (analyticsErrorMessage) {
                    <div class="dashboard-placeholder dashboard-error">{{ analyticsErrorMessage }}</div>
                } @else {
                    <div class="project-chart-wrapper skid-chart-scroll">
                        <div class="skid-chart-canvas" [style.width.px]="getSkidBreakdownChartWidthPx()">
                            <p-chart
                                type="bar"
                                [data]="skidBreakdownChartData"
                                [options]="skidBreakdownChartOptions"
                                [style]="{ height: '250px', width: '100%' }">
                            </p-chart>
                        </div>
                    </div>
                }
            </section>

            <p-dialog
                header="Hours Allocation by Equipment"
                [(visible)]="showSkidBreakdownFullscreen"
                [modal]="true"
                [dismissableMask]="true"
                [draggable]="false"
                [resizable]="false"
                appendTo="body"
                styleClass="skid-chart-dialog">
                <div class="skid-chart-dialog-body">
                    <div class="skid-chart-scroll skid-chart-scroll-fullscreen">
                        <div class="skid-chart-canvas skid-chart-canvas-fullscreen" [style.width.px]="getSkidBreakdownChartWidthPx(true)">
                            <p-chart
                                type="bar"
                                [data]="skidBreakdownChartData"
                                [options]="skidBreakdownChartOptions"
                                [style]="{ height: '70vh', width: '100%' }">
                            </p-chart>
                        </div>
                    </div>
                </div>
            </p-dialog>
            }
        </section>
        }
    }
</section>
